import { languageDisplayCode, LanguagesEnum } from '@/api/enums';
import {
  ContentItemType,
  CourseContent,
  CourseFormat,
  MaterialType,
} from '@/client/courses';
import { QuizType } from '@/client/quizzes';
import { TranslationFunctionType } from '@/common/types';
import { generateCourseTranslationsValidationSchema } from '@/utils/helpers';
import moment from 'moment';
import {
  AnyObject,
  array,
  boolean,
  date,
  number,
  object,
  string,
  TestContext,
} from 'yup';

export const createCourseSchema = (
  validOptions: string[],
  t: TranslationFunctionType,
) =>
  object({
    courseTranslations: generateCourseTranslationsValidationSchema(t),
    courseLanguages: array().when(['releaseDate'], ([releaseDate], schema) =>
      releaseDate === null
        ? schema.min(1, t('error.requiredField'))
        : schema.notRequired(),
    ),
    courseThumbnail: string().when(['releaseDate'], ([releaseDate], schema) =>
      releaseDate !== null
        ? schema.required(t('error.requiredField'))
        : schema.notRequired(),
    ),
    courseCreatedAt: date(),
    courseLastUpdatedAt: date(),
    customContent: boolean(),
    type: string(),
    categories: array().when(['type'], ([type], schema) =>
      type === MaterialType.POSTER
        ? schema.required(t('error.requiredField'))
        : schema.notRequired(),
    ),
    releaseDate: date().nullable(),
    plannerScheduleDate: date()
      .nullable()
      .test(
        'plannerScheduleDate-not-less-than-releaseDate',
        t('error.plannerScheduleDate.lesser.than.releaseDate'),
        function (value) {
          const { releaseDate } = this.parent;
          if (!releaseDate || !value) {
            return true;
          }
          return moment(value).isSameOrAfter(moment(releaseDate), 'day');
        },
      ),
    priority: number().nullable(),
    customAccount: string().when(['customContent'], ([customContent], schema) =>
      customContent === true
        ? schema.required()
        : schema.notRequired().nullable(),
    ),
    hubspotProperty: string().nullable(),
    hubspotDateProperty: string().nullable(),
    // Validate that each language have uploaded file
    courseFiles: array().test(
      'courseFilesRequired',
      t('error.requiredFile'),
      (schema, context: TestContext<AnyObject>) => {
        const value = context.parent;

        // Skip validation if the course has a future release date and is not previewable
        if (
          value?.releaseDate &&
          moment(value?.releaseDate).isAfter(moment(), 'day') &&
          !value?.isPreviewable
        ) {
          return true;
        }

        if (!value?.selfHosted) {
          return true;
        }

        if (value?.format === CourseFormat.CONTENTS) {
          return !!value?.courseContents?.every((content: CourseContent) => {
            // TODO: Remove this once feedback CRUD is implemented
            if (content?.quiz?.type === QuizType.FEEDBACK) {
              return true;
            }

            if (content?.type === ContentItemType.PRESENTATION) {
              const languages = content.presentation?.fullyTranslatedLanguages;

              return Array.isArray(languages) && languages.length > 0;
            }

            if (content?.quiz?.type === QuizType.TRADITIONAL) {
              const languages = content.quiz?.fullyTranslatedLanguages;

              return Array.isArray(languages) && languages.length > 0;
            } else {
              const languages = content.resource?.fullyTranslatedLanguages;

              return Array.isArray(languages) && languages.length > 0;
            }
          });
        }

        const courseLanguages =
          !value.id || value.selfHosted
            ? value.courseLanguages
            : [LanguagesEnum.EN];

        if (!value?.courseFiles?.length) {
          throw context.createError({
            path: 'courseFiles',
            message: t('error.requiredFile'),
          });
        }

        for (const language of courseLanguages) {
          for (const file of value.courseFiles) {
            const matchingFiles = Object.keys(file.translations).filter(
              (key: string) =>
                key === language &&
                file.translations[key]?.file &&
                (validOptions.includes(languageDisplayCode[key]) ||
                  !courseLanguages.includes(languageDisplayCode[key])),
            );

            if (!matchingFiles?.length) {
              throw context.createError({
                path: 'courseFiles',
                message: t('error.requiredFile'),
              });
            }
          }
        }

        return true;
      },
    ),
  });

export const forceCompleteCourseSchema = (t: TranslationFunctionType) =>
  object({
    score: number()
      .min(1, t('error.OneToOneHundredNumber'))
      .max(100, t('error.maxScore'))
      .required('error.requiredField'),
  });
